import { NotificationTopic } from "@/graphql"
import { type Activity, LinkType } from "@/types/activity"
import type { CurrentUserProject, Notification } from "@/utils/ApiClient"

function asRecord(value: unknown): Record<string, unknown> {
	if (typeof value === "object" && value !== null && !Array.isArray(value)) {
		return value as Record<string, unknown>
	}

	return {}
}

function optionalString(value: unknown): string | null {
	return typeof value === "string" && value.length > 0 ? value : null
}

function opportunityIds(metadata: Record<string, unknown>): string[] {
	const ids = Array.isArray(metadata.opportunityIds)
		? metadata.opportunityIds.filter(value => typeof value === "string" || typeof value === "number").map(String)
		: []

	const opportunityId = metadata.opportunityId

	if (typeof opportunityId === "string" || typeof opportunityId === "number") {
		ids.push(String(opportunityId))
	}

	return [...new Set(ids)]
}

export function notificationToActivity(notification: Notification, projects: CurrentUserProject[]): Activity {
	const metadata = {
		...notification.payload,
		...asRecord(notification.payload.metadata),
	}

	const projectId = optionalString(metadata.projectId)
	const project = projects.find(item => item.id === projectId)

	const shared = {
		id: notification.id,
		notificationTopic: notification.topic,
		title: notification.title,
		readAt: notification.readAt ?? null,
		projectId,
		opportunityIds: opportunityIds(metadata),
		actorName: optionalString(metadata.actorName),
		timestamp: new Date(notification.createdAt),
	}

	if (isProjectNotification(notification.topic)) {
		return {
			...shared,
			linkType: LinkType.PROJECT,
			project: project?.name ?? null,
			targetsCount: project?.targetsCount ?? null,
			comment: optionalString(metadata.comment),
			savedSearch: null,
			savedSearchId: null,
		}
	}

	return {
		...shared,
		linkType: LinkType.SAVED_SEARCH,
		project: null,
		savedSearch:
			optionalString(metadata.customViewName) ?? notification.title.match(/"(.+)"$/)?.[1] ?? notification.title,
		savedSearchId: optionalString(metadata.customViewId),
	}
}

export function isProjectNotification(notificationTopic: NotificationTopic): boolean {
	switch (notificationTopic) {
		case NotificationTopic.LikedOpportunity:
		case NotificationTopic.DislikedOpportunity:
		case NotificationTopic.CommentedOpportunity:
		case NotificationTopic.TargetCreated:
			return true
		default:
			return false
	}
}

export function isSearchNotification(notificationTopic: NotificationTopic): boolean {
	return notificationTopic === NotificationTopic.SavedSearchNewOpportunity
}
